import { IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import { useRouter } from 'next/router';
import { useCallback, useContext } from 'react';
import { LeadManagementContext } from '../../../../contexts/LeadManagementContextProvider';
import { AppContext } from '../../../../contexts/AppContextProvider';
import useLeadManagementActions from '../../../../hooks/useLeadManagementActions';
import { leadManagementRenterDetailsSectionToScrollToStore } from '../../../../lib/leadManagementStores';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';
import useQueryParamsNext from '../../../../hooks/useQueryParamsNext';
import useModal from '../../../../hooks/useModal';
import { ModalType } from '../../../../../src/types/ModalType';
import {
  CLICK_OTHER_ACTIONS_MENU_EVENT_TYPE_ID,
  emitClickOtherActionsMenuEvent,
} from '../../../../constants/clickstreamEvents/otherActionsMenu';
import { emitOpenReportProblemModalEvent } from '../../../../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../../../../constants/clickstreamEvents/globals';
import { useAuth } from '../../../../AuthProvider';
import useDevices from '../../../../hooks/useDevices';
import { InboxPageMobileView } from '../../../../types/InboxPageMobileView';
import useLeadManagementSecondaryActions from '../../../../hooks/useLeadManagementSecondaryActions';
import useLeadRestrictions from '../../../../hooks/useLeadRestrictions';
import useStreetEasyLeadManagementEnabled from '../../../../hooks/useStreetEasyLeadManagementEnabled';

type MoreActionsMenuProps = {
  conversationId: string;
  iconColor: 'neutral' | 'brand';
  setActiveRowId: (conversationId: string) => void;
};

export default function MoreActionsMenu({ conversationId, iconColor = 'brand', setActiveRowId }: MoreActionsMenuProps) {
  const { isImpersonator } = useContext(AppContext);
  const { isArchivedFolder, isReportedFolder } = useContext(LeadManagementContext);
  const { user } = useAuth();
  const { isMdAndBelow } = useDevices();
  const router = useRouter();
  const { updateQueryParams } = useQueryParamsNext();
  const {
    handleArchiveClick,
    handleCallClick,
    handleMarkAsUnread,
    handleMoveToInboxClick,
    handleMarkAsRead,
    handleOpenRenterMessagingModal,
    handleOptionsInOtherActionsClickstreamEvent,
  } = useLeadManagementActions({ conversationId });
  const { activeModal, setActiveModal } = useModal();

  const lead = useCachedLeadByConversationId(conversationId);
  const listingAlias = lead?.listingDetails?.listingAlias ?? '';
  const { canSendMessage } = useLeadRestrictions(lead);
  const isStreetEasyLeadManagementEnabled = useStreetEasyLeadManagementEnabled();
  const shouldShowMessageAction = !isStreetEasyLeadManagementEnabled || canSendMessage;

  const { latestContact, nextStepSecondaryActions, renterInfo, status } = lead || {};
  const { leadId, renterPhoneNumber } = renterInfo || {};
  const { isRead, messageDateMs } = latestContact || {};
  const { isConversationBlockedByZillow } = status || {};
  const handleApproveClick = useCallback(() => {
    setActiveRowId(conversationId);
    setActiveModal(ModalType.ApproveLeadModal);
    handleOptionsInOtherActionsClickstreamEvent('approve');
  }, [conversationId, setActiveRowId, setActiveModal, handleOptionsInOtherActionsClickstreamEvent]);

  const secondaryActions = useLeadManagementSecondaryActions(nextStepSecondaryActions, {
    onApprove: handleApproveClick,
  });

  const handleAddNoteClick = () => {
    leadManagementRenterDetailsSectionToScrollToStore.set('notepad');
    updateQueryParams({
      queryParamValues: {
        conversationId,
      },
      shouldReplaceHistory: true,
    });
    handleOptionsInOtherActionsClickstreamEvent('add_note');
  };

  const handleMessageClick = () => {
    handleOptionsInOtherActionsClickstreamEvent('message');

    if (isMdAndBelow) {
      router.push({
        pathname: '/inbox/[[...conversationSlug]]',
        query: {
          conversationSlug: [listingAlias, conversationId],
          mobileView: InboxPageMobileView.MessageList,
        },
      });
      return;
    }

    handleOpenRenterMessagingModal();
  };

  const handleOpenReportConversationModal = () => {
    setActiveRowId(conversationId);
    emitOpenReportProblemModalEvent({
      user,
      listing: { listingAlias },
      triggerLocation: TRIGGER_LOCATION.LeadManagementPage,
      lead: { leadId },
    });
    setActiveModal(ModalType.ReportConversationModal);
    handleOptionsInOtherActionsClickstreamEvent('report');
  };

  const handleOpenMenu = () => {
    setActiveRowId(conversationId);
    emitClickOtherActionsMenuEvent(user, { listingAlias }, { leadId });
  };

  const handleCloseMenu = () => {
    if (!activeModal) {
      setActiveRowId('');
    }
  };

  const handleCallMenuClick = () => {
    setActiveRowId(conversationId);
    handleCallClick();
  };

  const moveToInboxActionsMenu = (
    <Menu.Group aria-label="Conversation restoration actions" css={{ fontFamily: 'sansSerif' }}>
      {isReportedFolder && (
        <Menu.Item onClick={handleMoveToInboxClick}>
          <Menu.ItemLabel>Move to inbox</Menu.ItemLabel>
        </Menu.Item>
      )}

      {isArchivedFolder && (
        <Menu.Item onClick={handleMoveToInboxClick}>
          <Menu.ItemLabel>Unarchive</Menu.ItemLabel>
        </Menu.Item>
      )}
    </Menu.Group>
  );

  const moreActionsMenu = (
    <>
      {secondaryActions.length > 0 && (
        <Menu.Group aria-label="Lead approval actions">
          {secondaryActions.map(({ action, handler }) => (
            <Menu.Item key={action.link.linkId} onClick={handler} disabled={isImpersonator}>
              {action.link.text}
            </Menu.Item>
          ))}
        </Menu.Group>
      )}

      <Menu.Group aria-label="Contact actions">
        {shouldShowMessageAction && <Menu.Item onClick={handleMessageClick}>Message</Menu.Item>}
        {renterPhoneNumber && <Menu.Item onClick={handleCallMenuClick}>Call</Menu.Item>}
        <Menu.Item onClick={handleAddNoteClick}>Add note</Menu.Item>
        {isRead ? (
          <Menu.Item onClick={handleMarkAsUnread} disabled={isImpersonator}>
            Mark unread
          </Menu.Item>
        ) : (
          <Menu.Item
            onClick={() => handleMarkAsRead(messageDateMs ? String(messageDateMs) : '')}
            disabled={isImpersonator}
          >
            Mark read
          </Menu.Item>
        )}
      </Menu.Group>

      <Menu.Group aria-label="Conversation management actions">
        <Menu.Item onClick={handleArchiveClick} disabled={isImpersonator}>
          Archive
        </Menu.Item>
        <Menu.Item
          onClick={handleOpenReportConversationModal}
          disabled={isImpersonator || isConversationBlockedByZillow}
        >
          Report
        </Menu.Item>
      </Menu.Group>
    </>
  );

  return (
    <Menu
      content={isArchivedFolder || isReportedFolder ? moveToInboxActionsMenu : moreActionsMenu}
      dividers
      placement="bottom-start"
      onOpenChange={(isOpen) => (isOpen ? handleOpenMenu() : handleCloseMenu())}
      trigger={
        <IconButton
          data-eventid={CLICK_OTHER_ACTIONS_MENU_EVENT_TYPE_ID}
          emphasis="bare"
          icon={<IconMoreFilled />}
          shape="square"
          size="md"
          title="More options"
          tone={iconColor}
          data-testid="more-actions-menu-popper"
        />
      }
      css={{ fontFamily: 'sansSerif' }}
    />
  );
}
